/* eslint-disable no-magic-numbers */
import { Vector2 } from '@technology/web3d-plugin-threejs';
import { QuantizedMeshData } from '../types/quantized-mesh-data';

const QUANTIZED_MAX = 32767;
const VERTEX_COMPONENTS = 3;
const CONTIGUOUS_EPSILON = 1e-9;

/** One terrain elevation sample along a straight line. */
export interface TerrainProfileSample {
  /** Position along the line: 0 at its start, 1 at its end. */
  readonly fraction: number;
  readonly elevation: number;
  /** False when there is no terrain between this sample and the next one. */
  readonly connectsToNext: boolean;
}

/** The part `[t0, t1]` of a line (as fractions of it) that lies in tile `x`, `y`. */
export interface TileSpan {
  readonly x: number;
  readonly y: number;
  readonly t0: number;
  readonly t1: number;
}

export interface ProfilePiece {
  readonly t0: number;
  readonly h0: number;
  readonly t1: number;
  readonly h1: number;
}

/** Tiles at `zoom` crossed by the map-space line `a → b`, in order along it. */
export function tileSpansAlongLine(
  a: Vector2,
  b: Vector2,
  zoom: number,
  bounds: readonly Vector2[]
): TileSpan[] {
  const tilesPerAxis = 2 ** zoom;
  const sizeX = (bounds[1].x - bounds[0].x) / tilesPerAxis;
  const sizeY = (bounds[1].y - bounds[0].y) / tilesPerAxis;
  const ax = (a.x - bounds[0].x) / sizeX;
  const ay = (a.y - bounds[0].y) / sizeY;
  const bx = (b.x - bounds[0].x) / sizeX;
  const by = (b.y - bounds[0].y) / sizeY;
  const breaks = [0, 1, ...gridCrossings(ax, bx), ...gridCrossings(ay, by)];
  breaks.sort((p, q) => p - q);

  const spans: TileSpan[] = [];
  for (let i = 1; i < breaks.length; i++) {
    const t0 = breaks[i - 1];
    const t1 = breaks[i];
    if (t1 <= t0) {
      continue;
    }
    const mid = (t0 + t1) / 2;
    spans.push({
      x: Math.floor(ax + mid * (bx - ax)),
      y: Math.floor(ay + mid * (by - ay)),
      t0,
      t1,
    });
  }
  return spans;
}

function gridCrossings(from: number, to: number): number[] {
  const crossings: number[] = [];
  const high = Math.max(from, to);
  for (let k = Math.floor(Math.min(from, to)) + 1; k < high; k++) {
    crossings.push((k - from) / (to - from));
  }
  return crossings;
}

/**
 * Intersects the map-space line `a → b` with a decoded tile's triangles
 * within `span`. Triangles touching a vertex at `noDataHeight` are skipped,
 * leaving a gap instead of the base-height skirt the renderer draws there.
 */
export function sectionTile(
  data: QuantizedMeshData,
  tileMin: Vector2,
  tileMax: Vector2,
  a: Vector2,
  b: Vector2,
  span: TileSpan,
  noDataHeight: number
): ProfilePiece[] {
  const { vertexData, triangleIndices, header } = data;
  const vertexCount = vertexData.length / VERTEX_COMPONENTS;
  const toU = QUANTIZED_MAX / (tileMax.x - tileMin.x);
  const toV = QUANTIZED_MAX / (tileMax.y - tileMin.y);
  const au = (a.x - tileMin.x) * toU;
  const av = (a.y - tileMin.y) * toV;
  const du = (b.x - tileMin.x) * toU - au;
  const dv = (b.y - tileMin.y) * toV - av;
  const lengthSq = du * du + dv * dv;
  if (lengthSq === 0) {
    return [];
  }

  const heightRange = header.maxHeight - header.minHeight;
  const side = new Float64Array(vertexCount);
  const heights = new Float64Array(vertexCount);
  for (let i = 0; i < vertexCount; i++) {
    side[i] =
      du * (vertexData[i + vertexCount] - av) - dv * (vertexData[i] - au);
    // Same arithmetic as constructGeometry so no-data vertices compare equal.
    const height =
      header.minHeight +
      (vertexData[i + vertexCount * 2] / QUANTIZED_MAX) * heightRange;
    heights[i] = height === noDataHeight ? Number.NaN : height;
  }

  const pieces: ProfilePiece[] = [];
  const corners = [0, 0, 0];
  for (let t = 0; t < triangleIndices.length; t += VERTEX_COMPONENTS) {
    corners[0] = triangleIndices[t];
    corners[1] = triangleIndices[t + 1];
    corners[2] = triangleIndices[t + 2];
    const s0 = side[corners[0]];
    const s1 = side[corners[1]];
    const s2 = side[corners[2]];
    if ((s0 > 0 && s1 > 0 && s2 > 0) || (s0 < 0 && s1 < 0 && s2 < 0)) {
      continue;
    }
    if (corners.some((corner) => Number.isNaN(heights[corner]))) {
      continue;
    }

    let tA = Number.POSITIVE_INFINITY;
    let hA = 0;
    let tB = Number.NEGATIVE_INFINITY;
    let hB = 0;
    for (let edge = 0; edge < VERTEX_COMPONENTS; edge++) {
      const p = corners[edge];
      const q = corners[(edge + 1) % VERTEX_COMPONENTS];
      const sp = side[p];
      const sq = side[q];
      // An edge lying on the line (both zero) is covered by its neighbours.
      if ((sp > 0 && sq > 0) || (sp < 0 && sq < 0) || sp === sq) {
        continue;
      }
      const w = sp / (sp - sq);
      const u = vertexData[p] + w * (vertexData[q] - vertexData[p]);
      const v =
        vertexData[p + vertexCount] +
        w * (vertexData[q + vertexCount] - vertexData[p + vertexCount]);
      const along = ((u - au) * du + (v - av) * dv) / lengthSq;
      const height = heights[p] + w * (heights[q] - heights[p]);
      if (along < tA) {
        tA = along;
        hA = height;
      }
      if (along > tB) {
        tB = along;
        hB = height;
      }
    }

    const t0 = Math.max(tA, span.t0);
    const t1 = Math.min(tB, span.t1);
    if (t1 - t0 <= CONTIGUOUS_EPSILON) {
      continue;
    }
    const slope = (hB - hA) / (tB - tA);
    pieces.push({
      t0,
      h0: hA + (t0 - tA) * slope,
      t1,
      h1: hA + (t1 - tA) * slope,
    });
  }
  return pieces;
}

/**
 * Returns the parts of `[t0, t1]` not yet in `covered` and adds them to it.
 * `covered` holds disjoint intervals sorted by start.
 */
export function claimUncovered(
  covered: [number, number][],
  t0: number,
  t1: number
): [number, number][] {
  const gaps: [number, number][] = [];
  let start = t0;
  for (const [c0, c1] of covered) {
    if (c0 >= t1) {
      break;
    }
    if (c0 > start) {
      gaps.push([start, c0]);
    }
    start = Math.max(start, c1);
  }
  if (start < t1) {
    gaps.push([start, t1]);
  }
  covered.push(...gaps);
  covered.sort((p, q) => p[0] - q[0]);
  return gaps;
}

/** Runs at most `limit` of the tasks handed to it at once, in call order. */
export function limitConcurrency(
  limit: number
): <T>(task: () => Promise<T>) => Promise<T> {
  let free = limit;
  const waiting: (() => void)[] = [];
  return async (task) => {
    if (free > 0) {
      free--;
    } else {
      await new Promise<void>((resolve) => waiting.push(resolve));
    }
    try {
      return await task();
    } finally {
      const next = waiting.shift();
      if (next) {
        next();
      } else {
        free++;
      }
    }
  };
}

/** Orders pieces along the line and joins those that share an end point. */
export function piecesToSamples(
  pieces: readonly ProfilePiece[]
): TerrainProfileSample[] {
  const sorted = [...pieces].sort((p, q) => p.t0 - q.t0);
  const samples: {
    fraction: number;
    elevation: number;
    connectsToNext: boolean;
  }[] = [];
  for (const piece of sorted) {
    const last = samples.at(-1);
    if (last && piece.t0 <= last.fraction + CONTIGUOUS_EPSILON) {
      last.connectsToNext = true;
    } else {
      samples.push({
        fraction: piece.t0,
        elevation: piece.h0,
        connectsToNext: true,
      });
    }
    samples.push({
      fraction: piece.t1,
      elevation: piece.h1,
      connectsToNext: false,
    });
  }
  return samples;
}
